import { History } from 'history'
import React, { useCallback } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Dispatch } from 'redux'
import { DocumentsType } from 'types'
import {
  getAccountDetails,
  getAccountNotes,
  getAccountOwners,
  getAccountPositions,
  getActivities,
  getBalances,
  getCorrespondent,
  getMarginCalls,
  getMargins,
  getTradingRestrictions,
  getAuthorizedTraders,
  getInvestmentProfile,
  getDocuments,
  getAmountAvailable,
  getInterestedParties,
  searchAccounts,
  getEPreferences,
  getSecuremailLinks,
} from '../../actions'
import { useToastErrorHandler } from '../../hooks'
import { RootState } from '../../store'
import { SearchContext } from '../contexts/search'
import { SearchProps } from '../contexts/search/SearchContext'

interface SearchProviderComponentProps extends PropsFromRedux {
  children: JSX.Element | JSX.Element[]
}

const SearchProviderComponent = ({
  accountDetailsState,
  accountNotesState,
  accountOwnerState,
  accountPositionsState,
  accountSearchState,
  activitiesState,
  balanceState,
  correspondentState,
  tradingRestrictionsState,
  authorizedTradersState,
  investmentProfileState,
  documentsState,
  ePreferencesState,
  marginCallsState,
  marginsState,
  amountAvailableState,
  interestedPartiesState,
  securemailState,
  children,
  getAccountDetails,
  getAccountNotes,
  getAccountOwners,
  getAccountPositions,
  getActivities,
  getBalances,
  getCorrespondent,
  getMarginCalls,
  getMargins,
  getTradingRestrictions,
  getAuthorizedTraders,
  getInvestmentProfile,
  getDocuments,
  getEPreferences,
  getAmountAvailable,
  getInterestedParties,
  getSecuremailLinks,
  searchAccounts,
}: SearchProviderComponentProps) => {
  const onError = useToastErrorHandler()
  const searchStateAndActions: SearchProps = {
    accountDetailsState,
    accountNotesState,
    accountOwnerState,
    accountSearchState,
    accountPositionsState,
    activitiesState,
    balanceState,
    correspondentState,
    tradingRestrictionsState,
    authorizedTradersState,
    investmentProfileState,
    documentsState,
    ePreferencesState,
    amountAvailableState,
    marginCallsState,
    marginsState,
    interestedPartiesState,
    securemailState,
    getAccountDetails: useCallback(
      (accountNumber: string) => getAccountDetails(accountNumber, onError),
      [getAccountDetails, onError]
    ),
    getAccountNotes: useCallback(
      (accountNumber: string) => getAccountNotes(accountNumber, onError),
      [getAccountNotes, onError]
    ),
    getAccountOwners: useCallback(
      (accountNumber: string) => getAccountOwners(accountNumber, onError),
      [getAccountOwners, onError]
    ),
    getAccountPositions: useCallback(
      (accountNumber: string, processDate?: string) =>
        getAccountPositions(accountNumber, onError, processDate),
      [getAccountPositions, onError]
    ),
    getActivities: useCallback(
      (accountNumber: string, startDate: string, endDate: string) =>
        getActivities(accountNumber, onError, startDate, endDate),
      [getActivities, onError]
    ),
    getBalances: useCallback(
      (accountNumber: string, processDate?: string) =>
        getBalances(accountNumber, onError, processDate),
      [getBalances, onError]
    ),
    getCorrespondent: useCallback(
      (accountNumber: string) => getCorrespondent(accountNumber, onError),
      [getCorrespondent, onError]
    ),
    getMarginCalls: useCallback(
      (accountNumber: string) => getMarginCalls(accountNumber, onError),
      [getMarginCalls, onError]
    ),
    getMargins: useCallback(
      (accountNumber: string) => getMargins(accountNumber, onError),
      [getMargins, onError]
    ),
    getTradingRestrictions: useCallback(
      (accountNumber: string) => getTradingRestrictions(accountNumber, onError),
      [getTradingRestrictions, onError]
    ),
    getAuthorizedTraders: useCallback(
      (accountNumber: string) => getAuthorizedTraders(accountNumber, onError),
      [getAuthorizedTraders, onError]
    ),
    getInvestmentProfile: useCallback(
      (accountNumber: string) => getInvestmentProfile(accountNumber, onError),
      [getInvestmentProfile, onError]
    ),
    getDocuments: useCallback(
      (accountNumber: string) => getDocuments(accountNumber, onError),
      [getDocuments, onError]
    ),
    getEPreferences: useCallback(
      (accountNumber: string) => getEPreferences(accountNumber, onError),
      [getEPreferences, onError]
    ),
    getAmountAvailable: useCallback(
      (accountNumber: string) => getAmountAvailable(accountNumber, onError),
      [getAmountAvailable, onError]
    ),
    getInterestedParties: useCallback(
      (accountNumber: string) => getInterestedParties(accountNumber, onError),
      [getInterestedParties, onError]
    ),
    getSecuremailLinks: useCallback(
      (accountNumber: string, documentType: DocumentsType) =>
        getSecuremailLinks(accountNumber, documentType, onError),
      [getSecuremailLinks, onError]
    ),
    searchAccounts,
  }
  return (
    <SearchContext.Provider value={searchStateAndActions}>
      {children}
    </SearchContext.Provider>
  )
}

const mapStateToProps = ({
  accountDetailsReducer: accountDetailsState,
  accountNotesReducer: accountNotesState,
  accountOwnersReducer: accountOwnerState,
  accountPositionsReducer: accountPositionsState,
  accountsReducer: accountSearchState,
  activitiesReducer: activitiesState,
  balancesReducer: balanceState,
  correspondentReducer: correspondentState,
  marginCallsReducer: marginCallsState,
  marginsReducer: marginsState,
  tradingRestrictionsReducer: tradingRestrictionsState,
  authorizedTradersReducer: authorizedTradersState,
  investmentProfileReducer: investmentProfileState,
  documentsReducer: documentsState,
  ePreferencesReducer: ePreferencesState,
  amountAvailableReducer: amountAvailableState,
  interestedPartiesReducer: interestedPartiesState,
  securemailReducer: securemailState,
}: RootState) => ({
  accountDetailsState,
  accountNotesState,
  accountOwnerState,
  accountPositionsState,
  accountSearchState,
  activitiesState,
  balanceState,
  correspondentState,
  marginCallsState,
  marginsState,
  tradingRestrictionsState,
  authorizedTradersState,
  investmentProfileState,
  documentsState,
  ePreferencesState,
  amountAvailableState,
  interestedPartiesState,
  securemailState,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getAccountDetails: (
    accountNumber: string,
    onError: (error: string) => void
  ) => getAccountDetails(dispatch, accountNumber, onError),
  getAccountNotes: (accountNumber: string, onError: (error: string) => void) =>
    getAccountNotes(dispatch, accountNumber, onError),
  getAccountOwners: (accountNumber: string, onError: (error: string) => void) =>
    getAccountOwners(dispatch, accountNumber, onError),
  getAccountPositions: (
    accountNumber: string,
    onError: (error: string) => void,
    processDate?: string
  ) => getAccountPositions(dispatch, accountNumber, onError, processDate),
  getActivities: (
    accountNumber: string,
    onError: (error: string) => void,
    startDate: string,
    endDate: string
  ) => getActivities(accountNumber, dispatch, onError, startDate, endDate),
  getBalances: (
    accountNumber: string,
    onError: (error: string) => void,
    processDate?: string
  ) => getBalances(accountNumber, dispatch, onError, processDate),
  getCorrespondent: (accountNumber: string, onError: (error: string) => void) =>
    getCorrespondent(dispatch, accountNumber, onError),
  getMarginCalls: (accountNumber: string, onError: (error: string) => void) =>
    getMarginCalls(accountNumber, dispatch, onError),
  getMargins: (accountNumber: string, onError: (error: string) => void) =>
    getMargins(accountNumber, dispatch, onError),
  getTradingRestrictions: (
    accountNumber: string,
    onError: (error: string) => void
  ) => getTradingRestrictions(dispatch, accountNumber, onError),
  getAuthorizedTraders: (
    accountNumber: string,
    onError: (error: string) => void
  ) => getAuthorizedTraders(dispatch, accountNumber, onError),
  getInvestmentProfile: (
    accountNumber: string,
    onError: (error: string) => void
  ) => getInvestmentProfile(dispatch, accountNumber, onError),
  getInterestedParties: (
    accountNumber: string,
    onError: (error: string) => void
  ) => getInterestedParties(accountNumber, dispatch, onError),
  searchAccounts: (searchTerm: string, history: History) =>
    searchAccounts(dispatch, searchTerm, history),
  getDocuments: (accountNumber: string, onError: (error: string) => void) =>
    getDocuments(dispatch, accountNumber, onError),
  getEPreferences: (accountNumber: string, onError: (error: string) => void) =>
    getEPreferences(dispatch, accountNumber, onError),
  getAmountAvailable: (
    accountNumber: string,
    onError: (error: string) => void
  ) => getAmountAvailable(dispatch, accountNumber, onError),
  getSecuremailLinks: (
    accountNumber: string,
    documentType: DocumentsType,
    onError: (error: string) => void
  ) => getSecuremailLinks(dispatch, accountNumber, documentType, onError),
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export const SearchProvider = connector(SearchProviderComponent)
